// PaymentList.tsx
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { DateFilterTypesEnum, EntityType, PaymentService } from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import PaymentTopHeader from "../listings/top-listing-headers/payment-listing-top-header";
import PayableCard from "../listings/listing-cards/payables";
import SimpleSpinner from "../../components/loader";
import { UserLoginData } from "../../components/common/charts/Enums";
import DateHelper from "../../utils/DateHelper";
import { useLocation, useNavigate } from "react-router-dom";
import AddPaymentModal from "./AddPaymentModal";
import EditPayableModal from "./EditPayableModal";
import EditReceivableModal from "./EditReceivableModal";
import { usePermissions } from "../../hooks/usePermissions";


const DATE_FILTERS = {
  NEXT_30_DAYS: DateFilterTypesEnum.Next30Days,
  NEXT_3_MONTHS: DateFilterTypesEnum.Next3Months,
  NEXT_6_MONTHS: DateFilterTypesEnum.Next6Months,
};

interface PaymentListProps {
  isEntity?: boolean;
  entityId?: number;
  entityTypeId?: EntityType;
  className?: string;
  detailsActiveTab?: string;
  refreshTrigger?: number;
  onPaymentAdded?: () => void;

}

const PaymentList: React.FC<PaymentListProps> = ({ isEntity, entityId, entityTypeId, className, detailsActiveTab, refreshTrigger, onPaymentAdded }) => {
  const location = useLocation()
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true); // Start with loading as true
  const [fullyQualifiedFilter, setFullyQualifiedFilter] = useState("paidFlowTypeId == 1");
  const [activeTab, setActiveTab] = useState("Payable");
  const [dateFilterType, setDateFilterType] = useState<number | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(null);
  const [filterOptions, setFilterOptions] = useState([
    { title: "Payable", count: 0 },
    { title: "Receivable", count: 0 },
  ]);
  const [hasAnyPayments, setHasAnyPayments] = useState(false);
  const orgId = parseInt(sessionStorage.getItem(UserLoginData.organization_id));
  const navigate = useNavigate();
  const { canWrite } = usePermissions();

  const [paymentModalConfig, setPaymentModalConfig] = useState({
    isOpen: false,
    entityId: null as number | null,
    entityType: EntityType.Property
  });

  // ✅ NEW: State for Edit Modal
  const [editModalConfig, setEditModalConfig] = useState<{
    isOpen: boolean;
    data: any | null;
  }>({
    isOpen: false,
    data: null
  });

  // ✅ NEW: Receivable Modal State
  const [editReceivableModalConfig, setEditReceivableModalConfig] = useState<{
    isOpen: boolean;
    data: any | null;
  }>({
    isOpen: false,
    data: null
  });

  // ✅ NEW: Callback Handler
  const handleEditClick = useCallback((paymentData: any) => {
    setEditModalConfig({
      isOpen: true,
      data: paymentData
    });
  }, []);

  // ✅ NEW: Receivable Handler
  const handleEditReceivableClick = useCallback((paymentData: any) => {
    setEditReceivableModalConfig({
      isOpen: true,
      data: paymentData
    });
  }, []);

  // ✅ NEW: Ref to track if it's the first render
  const isInitialMount = useRef(true);
  
  // ✅ NEW: Ref to store the current request version to prevent stale data from being set
  const requestVersionRef = useRef(0);
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const urlPaymentMode = params.get("paymentMode"); // Payables, Receivables
    const urlPaidFlow = params.get("paidFlowTypeId"); // 1 or 2

    if (urlPaymentMode && urlPaidFlow) {
      // Set Tab
      const tab = urlPaymentMode === "Receivables" ? "Receivable" : "Payable";
      setActiveTab(tab);

      // Set Filter
      const filter = `paidFlowTypeId == ${urlPaidFlow}`;
      setFullyQualifiedFilter(filter);
    }
  }, [location.search]);

  const handleSort = (newDirection: "asc" | "desc") => {
    setSortDirection(newDirection);
  };



  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    const filter = tab === "Receivable" ? "paidFlowTypeId == 2" : "paidFlowTypeId == 1";
    setFullyQualifiedFilter(filter);
    // ✅ FIX: Sync the tab state to the URL so it's remembered when navigating back
    const urlParams = new URLSearchParams(location.search);
    urlParams.set("paymentMode", tab === "Receivable" ? "Receivables" : "Payables");
    urlParams.set("paidFlowTypeId", tab === "Receivable" ? "2" : "1");
    navigate(`?${urlParams.toString()}`, { replace: true });
  };

  const fetchPaymentData = useCallback(async (filter: string) => {
    const isDateFiltered = [DATE_FILTERS.NEXT_30_DAYS, DATE_FILTERS.NEXT_3_MONTHS, DATE_FILTERS.NEXT_6_MONTHS].includes(
      dateFilterType as number
    );

    if (isEntity && entityTypeId === EntityType.Property) {
      return await apiHelper(
        (params) => PaymentService.getApiPaymentDueAccountPropertylist(
          entityId!, undefined, undefined, undefined, undefined, undefined, filter,
          sortDirection ? `DueDate ${sortDirection}` : undefined,
          isDateFiltered ? dateFilterType : undefined,
          isDateFiltered ? DateHelper.getClientDate() : undefined,
          undefined, undefined,
          isDateFiltered ? "DueDate" : undefined
        ),
        { showNotification: false }
      );
    } else if (isEntity && entityTypeId === EntityType.Unit) {
      return await apiHelper(
        (params) => PaymentService.getApiPaymentDueAccountUnitlist(
          entityId!, undefined, undefined, undefined, undefined, undefined, filter,
          sortDirection ? `DueDate ${sortDirection}` : undefined,
          isDateFiltered ? dateFilterType : undefined,
          isDateFiltered ? DateHelper.getClientDate() : undefined,
          undefined, undefined,
          isDateFiltered ? "DueDate" : undefined
        ),
        { showNotification: false }
      );
    } else {
      return await apiHelper(
        PaymentService.getApiPaymentDueAccountOrganizationlist,
        { showNotification: false },
        orgId, undefined, undefined, // Using a reasonable page size like 100 for counts
        undefined, undefined, undefined, filter,
        sortDirection ? `DueDate ${sortDirection}` : undefined,
        isDateFiltered ? dateFilterType : undefined,
        isDateFiltered ? DateHelper.getClientDate() : undefined,
        undefined, undefined,
        isDateFiltered ? "DueDate" : undefined
      );
    }
  }, [dateFilterType, sortDirection, isEntity, entityId, entityTypeId, orgId]);

  const refreshData = useCallback(async () => {
    // ✅ NEW: Increment request version to mark this as the latest request
    requestVersionRef.current += 1;
    const currentRequestVersion = requestVersionRef.current;

    setLoading(true);
    setRows([]);
    try {
      const res = await fetchPaymentData(fullyQualifiedFilter);
      
      // ✅ NEW: Only update state if this is still the latest request
      if (currentRequestVersion !== requestVersionRef.current) {
        return;
      }

      setRows(res?.data?.success || []);
      setFilterOptions((prevOptions) =>
        prevOptions.map((option) =>
          option.title === activeTab
            ? { ...option, count: res?.data?.recordCount || 0 }
            : option
        )
      );
    } catch (err) {
      // ✅ NEW: Only log error if this is still the latest request
      if (currentRequestVersion === requestVersionRef.current) {
        console.error("Error fetching updated payment data", err);
      }
    } finally {
      // ✅ NEW: Only clear loading if this is still the latest request
      if (currentRequestVersion === requestVersionRef.current) {
        setLoading(false);
      }
    }
  }, [fetchPaymentData, fullyQualifiedFilter, activeTab]);

  // ✅ EFFECT 1: For initial load to get BOTH counts and default tab data
  // ✅ UPDATE EFFECT 1: For initial load in PaymentList
  useEffect(() => {
    const fetchInitialData = async () => {
      // ✅ NEW: Increment request version for initial fetch
      requestVersionRef.current += 1;
      const currentRequestVersion = requestVersionRef.current;

      // 1. First, parse the URL to determine the correct starting tab
      const params = new URLSearchParams(location.search);
      const urlPaymentMode = params.get("paymentMode");
      const urlPaidFlow = params.get("paidFlowTypeId");

      let initialTab = "Payable";
      let initialFilter = "paidFlowTypeId == 1";

      if (urlPaymentMode && urlPaidFlow) {
        initialTab = urlPaymentMode === "Receivables" ? "Receivable" : "Payable";
        initialFilter = `paidFlowTypeId == ${urlPaidFlow}`;

        // Update states immediately
        setActiveTab(initialTab);
        setFullyQualifiedFilter(initialFilter);
      }

      setLoading(true);
      try {
        // 2. Fetch counts for both tabs
        const [payableRes, receivableRes] = await Promise.all([
          fetchPaymentData("paidFlowTypeId == 1"),
          fetchPaymentData("paidFlowTypeId == 2"),
        ]);

        // ✅ NEW: Only update state if this is still the latest request
        if (currentRequestVersion !== requestVersionRef.current) {
          return;
        }

        // 3. Set rows based on the initialTab we determined above
        if (initialTab === "Receivable") {
          setRows(receivableRes?.data?.success || []);
        } else {
          setRows(payableRes?.data?.success || []);
        }

        setFilterOptions([
          { title: "Payable", count: payableRes?.data?.recordCount || 0 },
          { title: "Receivable", count: receivableRes?.data?.recordCount || 0 },
        ]);
      } catch (err) {
        // ✅ NEW: Only log error if this is still the latest request
        if (currentRequestVersion === requestVersionRef.current) {
          console.error("Error fetching initial payment data", err);
        }
      } finally {
        // ✅ NEW: Only clear loading if this is still the latest request
        if (currentRequestVersion === requestVersionRef.current) {
          setLoading(false);
        }
      }
    };

    fetchInitialData();
  }, []); // Keep as empty array

  // ✅ EFFECT 2: For updates when user changes tab, sorts, or filters
  useEffect(() => {
    // Skip this effect on the very first render
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    refreshData();
  }, [fullyQualifiedFilter, dateFilterType, sortDirection, refreshData]);

  useEffect(() => {
    if (filterOptions.some((opt) => opt.count > 0)) {
      setHasAnyPayments(true);
    }
  }, [filterOptions]);

  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      refreshData();
    }
  }, [refreshTrigger, refreshData]);

  const MENU_ITEMS = useMemo(
    () => [
      {
        label: "All Payments",
        action: () => {
          setDateFilterType(null);
          setSortDirection(null);
        },
      },
      {
        label: "Next 30 Days",
        action: () => setDateFilterType(DATE_FILTERS.NEXT_30_DAYS),
      },
      {
        label: "Next 3 months",
        action: () => setDateFilterType(DATE_FILTERS.NEXT_3_MONTHS),
      },
      {
        label: "Next 6 months",
        action: () => setDateFilterType(DATE_FILTERS.NEXT_6_MONTHS),
        isDivider: false,
        isDestructive: false,
      },
    ],
    []
  );

  return (
    <div className="content-wrapper container-fluid">
      {hasAnyPayments && (
        <PaymentTopHeader
          className={className}
          PageHeading="Payments"
          Count={filterOptions.reduce((sum, option) => sum + option.count, 0)}
          ButtonName="Add Payment"
          handleTabChange={handleTabChange}
          filterOptions={filterOptions}
          activeTab={activeTab}
          menuItems={MENU_ITEMS}
          handleSort={handleSort}
          currentSortDirection={sortDirection || "asc"}
          disableTabs={loading}
          showButton={canWrite("Payments")}
          onClick={() => isEntity ?
            setPaymentModalConfig({
              isOpen: true,
              entityId: Number(entityId),
              entityType: entityTypeId
            })
            : navigate('/payment/create')
          }
        />
      )}

      {loading ? (
        <div className="d-flex justify-content-center align-items-center flex-grow-1 h-100 w-100">
          <SimpleSpinner myClassName="Icon50" />
        </div>
      ) : (
        <PayableCard
          id="Payments"
          className={className}
          isFooter={true}
          onRefresh={onPaymentAdded}
          dataHookState={{
            rows,
            loading,
            fetchData: refreshData,
          }}

          onEditPayableClick={handleEditClick}
          onEditReceivableClick={handleEditReceivableClick}
          hasAnyPayments={hasAnyPayments}

          isEntity={isEntity}
          entityId={entityId}
          entityTypeId={entityTypeId}
          onAddClick={() => isEntity ? setPaymentModalConfig({
            isOpen: true,
            entityId: Number(entityId),
            entityType: entityTypeId
          }) : navigate('/payment/create')}
        />
      )}


      {/* Add PAYMENT*/}
      {
        paymentModalConfig.isOpen && (
          <AddPaymentModal
            isOpen={paymentModalConfig.isOpen}
            onClose={() => {
              setPaymentModalConfig(prev => ({ ...prev, isOpen: false }));
              refreshData();
              if (onPaymentAdded) onPaymentAdded();
            }}
            entityIdProp={paymentModalConfig.entityId || undefined}
            entityTypeIdProp={paymentModalConfig.entityType}
            defaultPaymentMode={entityTypeId === EntityType.Tenant ? "2" : "1"}
          />
        )
      }

      {/* ✅ ADD EDIT PAYABLE MODAL */}
      {editModalConfig.isOpen && (
        <EditPayableModal
          isOpen={editModalConfig.isOpen}
          onClose={() => setEditModalConfig({ isOpen: false, data: null })}
          data={editModalConfig.data}
          onSuccess={() => {
            refreshData();
            if (onPaymentAdded) onPaymentAdded();
          }}
        />
      )}

      {/* ✅ Edit Receivable Modal */}
      {editReceivableModalConfig.isOpen && (
        <EditReceivableModal
          isOpen={editReceivableModalConfig.isOpen}
          onClose={() => setEditReceivableModalConfig({ isOpen: false, data: null })}
          data={editReceivableModalConfig.data}
          onSuccess={refreshData}
        />
      )}

      {/* Manage Auto Pay Sidebar */}

    </div>
  );
};

export default PaymentList;